import React, { useId } from 'react';
import {
  KEYFRAME_EASINGS, setKeyframeEasing, type EasedKeyframe,
} from '../../items/animation/keyframe-easing';
import { useCurrentPlayerFrame } from '../../utils/use-current-player-frame';

const labels = {
  linear: '일정하게 (Linear)',
  'ease-in': '서서히 빠르게 (Ease in)',
  'ease-out': '서서히 느리게 (Ease out)',
  'ease-in-out': '부드럽게 시작·끝 (Ease in out)',
  hold: '유지 후 전환 (Hold)',
};

/** Marker seek and inspector share the playhead. Native select supports keyboard operation. */
export function KeyframeEasingControl<T extends EasedKeyframe>({
  keyframes, from, maxLocalFrame, kind, onChange,
}: {
  keyframes: T[];
  from: number;
  maxLocalFrame: number;
  kind: '위치' | '줌';
  onChange: (next: T[]) => void;
}) {
  const id = useId();
  const frame = Math.round(useCurrentPlayerFrame() - from);
  const point = keyframes.find((entry) => Math.round(entry.frame) === frame);
  const hasNext = point && keyframes.some((entry) => entry.frame > point.frame);
  const disabled = !hasNext || frame < 0 || frame > maxLocalFrame;
  return (
    <div className="flex flex-col gap-4xs">
      <label htmlFor={id} className="text-caption-small text-sys-cont-neutral-lightest">
        {kind} 곡선 · 이 키프레임에서 다음까지
      </label>
      <select
        id={id}
        aria-label={`${kind} 키프레임 곡선`}
        aria-describedby={`${id}-hint`}
        disabled={disabled}
        value={point?.easing ?? 'linear'}
        className="w-full rounded border border-current bg-transparent p-4xs text-caption-small disabled:opacity-50"
        onChange={(event) => {
          const easing = KEYFRAME_EASINGS.find((entry) => entry === event.target.value);
          if (disabled || !easing) return;
          const next = setKeyframeEasing(keyframes, frame, easing);
          if (next !== keyframes) onChange(next);
        }}
      >
        {KEYFRAME_EASINGS.map((easing) => (
          <option key={easing} value={easing}>{labels[easing]}</option>
        ))}
      </select>
      <span id={`${id}-hint`} className="text-caption-small text-sys-cont-neutral-lightest">
        {!point ? '마커를 선택하거나 키프레임으로 이동해 주세요.' :
          !hasNext ? '마지막 키프레임에는 다음 구간이 없습니다.' :
          '선택한 곡선은 다음 키프레임에 도달할 때까지 적용됩니다.'}
      </span>
    </div>
  );
}
